import Box from "@mui/material/Box";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useIsIntegrated from "../../../api/hooks/useIsIntegrated";
import { issuesKeys } from "../../../api/queryKeys";
import AppPage from "../../../components/structure/AppPage";
import { JIRA_PRIORITY } from "../../../utils/constants";
import { searchObjectProperties } from "../../../utils/search";
import IssuesTable from "./components/IssuesTable";
import TrackingFilters from "./components/TrackingFilters";
import TrackingToolRequiredModal from "./components/TrackingToolRequiredModal";

const TrackingView = () => {
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const { fetchJiraIssues: fetchJiraIssuesApi } = useAPI();
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("jira");
  const [openModal, setOpenModal] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
    priorities: {},
    labels: {},
    statuses: {},
    projects: {},
    assignee: {},
    statusCategories: {},
    controlStatus: {},
  });
  const [filterOption, setFilterOption] = useState({
    priorities: ["All", ...Object.values(JIRA_PRIORITY)],
    labels: [],
    statuses: [],
    projects: [],
    assignee: [],
    statusCategories: [],
    controlStatus: [],
  });

  const { data: allIssues = null, isLoading: issusIsLoading } = useQuery({
    queryKey: issuesKeys.all,
    queryFn: () => fetchJiraIssuesApi(),
    select: (data) => data.data,
    enabled: isIntegrated,
  });

  useEffect(() => {
    if (allIssues) {
      setFilteredIssues(allIssues);
      let allLabels = [];
      let allAssignee = [];
      let allStatuses = [];
      let allProjects = {};
      let allStatusCategories = [];
      let allControlStatus = [];
      allIssues.forEach((issue) => {
        if (
          !allControlStatus.includes(issue.control_status) &&
          issue.control_status
        ) {
          allControlStatus.push(issue.control_status);
        }

        if (!allStatusCategories.includes(issue.status_category)) {
          allStatusCategories.push(issue.status_category);
        }
        if (!allProjects[issue.project.key]) {
          allProjects[issue.project.key] = issue.project;
        }
        issue.labels.forEach((label) => {
          if (!allLabels.includes(label) && label !== "breeze") {
            allLabels.push(label);
          }
        });
        if (!allStatuses.includes(issue.status)) {
          const status = issue.status;
          allStatuses.push(status);
        }

        if (!allAssignee.includes(issue.assignee)) {
          allAssignee.push(issue.assignee);
        }
      });
      setFilterOption({
        ...filterOption,
        projects: [{ key: "All", name: "All" }, ...Object.values(allProjects)],
        labels: ["All", ...Array.from(new Set(allLabels))],
        assignee: ["All", ...allAssignee],
        statuses: ["All", ...Array.from(new Set(allStatuses))],
        statusCategories: ["All", ...allStatusCategories],
        controlStatus: ["All", ...allControlStatus],
      });
    }
  }, [allIssues]);

  useEffect(() => {
    if (allIssues) {
      let filtered = allIssues;
      if (Object.keys(appliedFilters.labels).length > 0) {
        const labels_filter = Object.keys(appliedFilters.labels).filter(
          (key) => appliedFilters.labels[key]
        );
        if (labels_filter.length > 0) {
          filtered = filtered.filter((row) =>
            labels_filter.some((label) => row.labels.includes(label))
          );
        }
      }
      if (Object.keys(appliedFilters.priorities).length > 0) {
        const priorities_filter = Object.keys(appliedFilters.priorities).filter(
          (key) => appliedFilters.priorities[key]
        );
        if (priorities_filter.length > 0) {
          filtered = filtered.filter((row) =>
            priorities_filter.includes(row.priority.toString())
          );
        }
      }
      if (Object.keys(appliedFilters.statuses).length > 0) {
        const statuses_filter = Object.keys(appliedFilters.statuses).filter(
          (key) => appliedFilters.statuses[key]
        );
        if (statuses_filter.length > 0) {
          filtered = filtered.filter((row) => {
            return statuses_filter.includes(row.status.toString());
          });
        }
      }
      if (Object.keys(appliedFilters.assignee).length > 0) {
        const assignee_filter = Object.keys(appliedFilters.assignee).filter(
          (key) => appliedFilters.assignee[key]
        );
        if (assignee_filter.length > 0) {
          filtered = filtered.filter((row) =>
            assignee_filter.includes(row.assignee)
          );
        }
      }
      if (Object.keys(appliedFilters.projects).length > 0) {
        const projects_filter = Object.keys(appliedFilters.projects).filter(
          (key) => appliedFilters.projects[key]
        );
        if (projects_filter.length > 0) {
          filtered = filtered.filter((row) =>
            projects_filter.includes(row.project.key)
          );
        }
      }
      if (Object.keys(appliedFilters.statusCategories).length > 0) {
        const statusCategories_filter = Object.keys(
          appliedFilters.statusCategories
        ).filter((key) => appliedFilters.statusCategories[key]);
        if (statusCategories_filter.length > 0) {
          filtered = filtered.filter((row) =>
            statusCategories_filter.includes(row.status_category)
          );
        }
      }
      if (Object.keys(appliedFilters.controlStatus).length > 0) {
        const controlStatus_filter = Object.keys(
          appliedFilters.controlStatus
        ).filter((key) => appliedFilters.controlStatus[key]);
        if (controlStatus_filter.length > 0) {
          filtered = filtered.filter((row) =>
            controlStatus_filter.includes(row.control_status)
          );
        }
      }

      if (appliedFilters.searchString !== "") {
        const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
        filtered = filtered.filter((row) =>
          searchObjectProperties(row, lowerCaseSearchString)
        );
      }

      setFilteredIssues(filtered);
    }
  }, [
    allIssues,
    appliedFilters.labels,
    appliedFilters.priorities,
    appliedFilters.searchString,
    appliedFilters.statuses,
    appliedFilters.assignee,
    appliedFilters.projects,
    appliedFilters.statusCategories,
    appliedFilters.controlStatus,
  ]);

  useEffect(() => {
    if (!isIntegrated && !isIntegratedLoading) {
      setOpenModal(true);
    }
  }, [isIntegrated, isIntegratedLoading]);

  return (
    <AppPage title="Tracking">
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <TrackingFilters
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
            filterOption={filterOption}
          />

          <Box>
            <IssuesTable
              issues={filteredIssues}
              setSelectedIssue={setSelectedIssue}
              loading={issusIsLoading || isIntegratedLoading}
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) =>
                setFilterModel(newFilterModel)
              }
            />
          </Box>
        </Box>

        <TrackingToolRequiredModal
          open={openModal}
          handleClose={() => {
            setOpenModal(false);
          }}
        />
      </Box>
    </AppPage>
  );
};
export default TrackingView;
